import { useCallback, useMemo } from 'react';

import {
	getAllCommands,
	getCommandsByCategory,
	searchCommands,
	getCommandsByPattern,
} from '@/components/commands/registry';
import type { Command } from '@/components/commands/types';

export const useCommands = () => {
	const allCommands = useMemo(() => getAllCommands(), []);

	const getCommandsByCategoryCallback = useCallback((category: string) => {
		return getCommandsByCategory(category);
	}, []);

	const searchCommandsCallback = useCallback((query: string) => {
		return searchCommands(query);
	}, []);

	const getCommandsByPatternCallback = useCallback((pattern: string) => {
		return getCommandsByPattern(pattern);
	}, []);

	const processCommandQuery = useCallback((query: string): Command[] => {
		if (!query.startsWith('/')) {
			return [];
		}

		const commandQuery = query.slice(1);
		const commandQueryLower = commandQuery.toLowerCase();

		// Check for prefix commands with inline args (e.g., "/task Call Lynn")
		const prefixCommand = allCommands.find(cmd =>
			cmd.prefix && (
				commandQueryLower === cmd.prefix
				|| commandQueryLower.startsWith(cmd.prefix + ' ')
			),
		);

		if (prefixCommand) {
			const args = commandQuery.slice(prefixCommand.prefix!.length).trim();

			if (args && prefixCommand.inlineAction) {
				const syntheticCommand: Command = {
					...prefixCommand,
					title: `Quick Task: ${args}`,
					action: (closeSearch) => {
						prefixCommand.inlineAction!(args, closeSearch);
					},
				};
				return [syntheticCommand];
			}

			return [prefixCommand];
		}

		if (commandQueryLower === 'new' || commandQueryLower === '') {
			return getCommandsByCategoryCallback('create');
		} else if (commandQueryLower.startsWith('new ')) {
			const filterQuery = commandQueryLower.slice(4);
			return searchCommandsCallback(filterQuery).filter(cmd => cmd.category === 'create');
		} else {
			return searchCommandsCallback(commandQueryLower);
		}
	}, [allCommands, getCommandsByCategoryCallback, searchCommandsCallback]);

	return {
		allCommands,
		getCommandsByCategory: getCommandsByCategoryCallback,
		searchCommands: searchCommandsCallback,
		getCommandsByPattern: getCommandsByPatternCallback,
		processCommandQuery,
	};
};
